import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Alert,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Domain as DomainIcon,
} from '@mui/icons-material';
import { useAxiosClient } from '../utils/useAxiosClient';

interface WhitelistedDomain {
  id: number;
  domain: string;
  organizationId: number;
  organizationName: string;
  defaultRole: string;
  isTrialDomain: boolean;
  trialDurationDays: number;
  isActive: boolean;
  notes?: string;
  createdAt?: string;
}

interface WhitelistedDomainsProps {
  organizationId: number;
  organizationName: string;
}

const WhitelistedDomains: React.FC<WhitelistedDomainsProps> = ({ 
  organizationId, 
  organizationName 
}) => {
  const axiosClient = useAxiosClient();
  const [domains, setDomains] = useState<WhitelistedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingDomain, setEditingDomain] = useState<WhitelistedDomain | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [newDomain, setNewDomain] = useState({
    domain: '',
    defaultRole: 'Viewer',
    isTrialDomain: false,
    trialDurationDays: 30,
    isActive: true,
    notes: '',
  });

  // Fetch domains for this organization
  useEffect(() => {
    fetchDomains();
  }, [organizationId]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get('/api/admin/whitelisted-domains');
      // Filter domains for this organization
      const orgDomains = response.data.domains.filter(
        (domain: WhitelistedDomain) => domain.organizationId === organizationId
      );
      setDomains(orgDomains);
    } catch (error) {
      console.error('Error fetching domains:', error);
      setError('Failed to fetch whitelisted domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    try {
      const domainData = {
        ...newDomain,
        organizationId,
      };
      
      const response = await axiosClient.post('/api/admin/whitelisted-domains', domainData);
      
      setDomains([...domains, response.data.domain]);
      setOpenAddDialog(false);
      setNewDomain({
        domain: '',
        defaultRole: 'Viewer',
        isTrialDomain: false,
        trialDurationDays: 30,
        isActive: true,
        notes: '',
      });
      setSuccess(`Successfully added domain ${newDomain.domain}`);
      setError('');
    } catch (error: any) {
      console.error('Error adding domain:', error);
      setError(error.response?.data?.error || 'Failed to add domain');
    }
  };

  const handleEditDomain = async () => {
    if (!editingDomain) return;
    
    try {
      const response = await axiosClient.put(
        `/api/admin/whitelisted-domains/${editingDomain.id}`,
        editingDomain
      );
      
      setDomains(domains.map(d => d.id === editingDomain.id ? response.data.domain : d));
      setOpenEditDialog(false);
      setEditingDomain(null);
      setSuccess(`Successfully updated domain ${editingDomain.domain}`);
      setError('');
    } catch (error: any) {
      console.error('Error updating domain:', error);
      setError(error.response?.data?.error || 'Failed to update domain');
    }
  };

  const handleDeleteDomain = async (domainId: number, domainName: string) => {
    if (!window.confirm(`Are you sure you want to delete domain "${domainName}"?`)) return;
    
    try {
      await axiosClient.delete(`/api/admin/whitelisted-domains/${domainId}`);
      setDomains(domains.filter(d => d.id !== domainId));
      setSuccess(`Successfully deleted domain ${domainName}`);
      setError('');
    } catch (error: any) {
      console.error('Error deleting domain:', error);
      setError(error.response?.data?.error || 'Failed to delete domain');
    }
  };

  const openEditDomainDialog = (domain: WhitelistedDomain) => {
    setEditingDomain({ ...domain });
    setOpenEditDialog(true);
  };

  const roleOptions = ['Viewer', 'Brand Editor', 'Billing Admin', 'Owner'];

  if (loading) {
    return <Typography>Loading whitelisted domains...</Typography>;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DomainIcon />
          <Typography variant="h6">Whitelisted Domains</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddDialog(true)}
        >
          Add Domain
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>Default Role</TableCell>
                <TableCell>Trial</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domains.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">
                      No whitelisted domains. Add a domain to automatically grant access to users from that domain.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                domains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        *@{domain.domain}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={domain.defaultRole} size="small" />
                    </TableCell>
                    <TableCell>
                      {domain.isTrialDomain ? (
                        <Chip 
                          label={`${domain.trialDurationDays} days`} 
                          size="small" 
                          color="warning" 
                        />
                      ) : (
                        <Chip label="No trial" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={domain.isActive ? 'Active' : 'Inactive'} 
                        size="small" 
                        color={domain.isActive ? 'success' : 'default'} 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {domain.notes || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => openEditDomainDialog(domain)}
                        title="Edit domain"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDomain(domain.id, domain.domain)}
                        title="Delete domain"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Domain Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Whitelisted Domain</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Users from this domain will automatically get access to {organizationName} with the specified role.
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Domain"
                placeholder="company.com"
                value={newDomain.domain}
                onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                fullWidth
                helperText="Enter domain without @ or wildcards (e.g., company.com)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Default Role</InputLabel>
                <Select
                  value={newDomain.defaultRole}
                  onChange={(e) => setNewDomain({ ...newDomain, defaultRole: e.target.value })}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newDomain.isTrialDomain}
                    onChange={(e) => setNewDomain({ ...newDomain, isTrialDomain: e.target.checked })}
                  />
                }
                label="Trial Access"
              />
            </Grid>

            {newDomain.isTrialDomain && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Trial Duration (Days)"
                  type="number"
                  value={newDomain.trialDurationDays}
                  onChange={(e) => setNewDomain({ ...newDomain, trialDurationDays: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newDomain.isActive}
                    onChange={(e) => setNewDomain({ ...newDomain, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Notes"
                multiline
                rows={2}
                value={newDomain.notes}
                onChange={(e) => setNewDomain({ ...newDomain, notes: e.target.value })}
                fullWidth
                placeholder="Optional notes about this domain"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddDomain} 
            variant="contained"
            disabled={!newDomain.domain.trim()}
          >
            Add Domain
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Domain Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Whitelisted Domain</DialogTitle>
        <DialogContent>
          {editingDomain && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Domain"
                  value={editingDomain.domain}
                  onChange={(e) => setEditingDomain({ ...editingDomain, domain: e.target.value })}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Default Role</InputLabel>
                  <Select
                    value={editingDomain.defaultRole}
                    onChange={(e) => setEditingDomain({ ...editingDomain, defaultRole: e.target.value })}
                  >
                    {roleOptions.map((role) => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingDomain.isTrialDomain}
                      onChange={(e) => setEditingDomain({ ...editingDomain, isTrialDomain: e.target.checked })}
                    />
                  }
                  label="Trial Access"
                />
              </Grid>

              {editingDomain.isTrialDomain && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Trial Duration (Days)"
                    type="number"
                    value={editingDomain.trialDurationDays}
                    onChange={(e) => setEditingDomain({ ...editingDomain, trialDurationDays: parseInt(e.target.value) })}
                    fullWidth
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingDomain.isActive}
                      onChange={(e) => setEditingDomain({ ...editingDomain, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  multiline
                  rows={2}
                  value={editingDomain.notes || ''}
                  onChange={(e) => setEditingDomain({ ...editingDomain, notes: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleEditDomain} 
            variant="contained"
            disabled={!editingDomain?.domain.trim()}
          >
            Update Domain
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhitelistedDomains;
